import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { productApi, Product, getImageUrl } from '../services/api';
import './Products.css';

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category') || 'all';
  const [selectedCategory, setSelectedCategory] = useState<string>(categoryFromUrl);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const category = searchParams.get('category') || 'all';
    setSelectedCategory(category);
  }, [searchParams]);

  const fetchProducts = async () => {
    try {
      const response = await productApi.getAll(true);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const categorySet = new Set(products.map(p => p.category).filter((cat): cat is string => Boolean(cat)));
  const categories = ['all', ...Array.from(categorySet)];
  
  let filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  // 应用搜索过滤
  if (searchQuery.trim()) {
    filteredProducts = filteredProducts.filter(p => 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  const getCategoryDisplayName = (category: string) => {
    if (category === 'all') return 'All Products';
    return category;
  };

  return (
    <div className="products-page">
      <div className="products-container">
        {/* 页面标题和面包屑 */}
        <div className="products-header">
          <h1>Products</h1>
          <div className="breadcrumbs">
            <Link to="/">Home</Link>
            <span> &gt; </span>
            <span className="current-category">{getCategoryDisplayName(selectedCategory)}</span>
          </div>
        </div>

        <div className="products-content">
          {/* 左侧分类栏 */}
          <div className="products-sidebar">
            <h2>Product Category</h2>
            <ul className="category-list">
              {categories.map((category) => (
                <li key={category}>
                  <Link
                    to={category === 'all' ? '/products' : `/products?category=${encodeURIComponent(category)}`}
                    className={`category-item ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {getCategoryDisplayName(category)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 右侧产品列表 */}
          <div className="products-main">
            {/* 搜索和视图控制 */}
            <div className="products-toolbar">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="toolbar-right">
                <span className="product-count">{filteredProducts.length} products</span>
                <div className="view-toggle">
                  <button
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M1 1h6v6H1V1zm8 0h6v6H9V1zM1 9h6v6H1V9zm8 0h6v6H9V9z"/>
                    </svg>
                  </button>
                  <button
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List View"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M1 2h14v2H1V2zm0 4h14v2H1V6zm0 4h14v2H1v-2zm0 4h14v2H1v-2z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* 产品列表 */}
            {loading ? (
              <div className="loading">Loading products...</div>
            ) : filteredProducts.length > 0 ? (
              <div className={`products-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                {filteredProducts.map((product) => (
                  <div key={product.id} className="product-card">
                    {product.imageUrl ? (
                      <div className="product-image">
                        <img src={getImageUrl(product.imageUrl)} alt={product.name} />
                      </div>
                    ) : (
                      <div className="product-placeholder">No Image</div>
                    )}
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      {product.description && (
                        <p className="product-description">{product.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-products">
                <p>No products found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
